'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { Section } from '@/components/ui/Section';
import { Container } from '@/components/ui/Container';
import { Heading } from '@/components/ui/Heading';
import { Text } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { normalizePartnerName } from '@/lib/image-alt-helper';
import { type Locale, getLocalePath, locales as supportedLocales } from '@/lib/i18n';
import { getIntegrationPath, getRoute } from '@/lib/routes';
import { trackCTAClick } from '@/lib/cta-tracking';
import styles from './IntegrationsSection.module.css';

interface Integration {
  name: string;
  logo: string;
  slug: string;
}

interface IntegrationsSectionProps {
  translations: {
    label: string;
    title: string;
    description: string;
    cta?: string;
  };
  altTexts?: Record<string, string>;
  background?: 'primary' | 'secondary';
  locale?: Locale;
  ctaId?: string;
  ctaPosition?: string;
  /** When false, integration cards render as plain divs instead of links */
  linkCards?: boolean;
}

const integrations: Integration[] = [
  { name: 'Lightspeed', logo: '/images/integrations/pos/lightspeed.webp', slug: 'lightspeed' },
  { name: 'unTill', logo: '/images/integrations/pos/untill.webp', slug: 'untill' },
  { name: 'Oracle', logo: '/images/integrations/pos/oracle.webp', slug: 'oracle' },
  { name: 'Deliverect', logo: '/images/integrations/pos/deliverect.webp', slug: 'deliverect' },
  { name: 'MplusKASSA', logo: '/images/integrations/pos/mpluskassa.webp', slug: 'mpluskassa' },
  { name: 'Trivec', logo: '/images/integrations/pos/trivec.webp', slug: 'trivec' },
  { name: 'Gastrofix', logo: '/images/integrations/pos/gastrofix.webp', slug: 'gastrofix' },
  { name: 'NuvoPOS', logo: '/images/integrations/pos/nuvopos.webp', slug: 'nuvopos' },
  { name: 'Vectron', logo: '/images/integrations/pos/vectron.webp', slug: 'vectron' },
  { name: 'Twelve', logo: '/images/integrations/pos/twelve.webp', slug: 'twelve' },
  { name: 'NCR Aloha', logo: '/images/integrations/pos/ncr-aloha.webp', slug: 'ncr-aloha' },
  { name: 'CashDesk', logo: '/images/integrations/pos/cashdesk.webp', slug: 'cashdesk' },
  { name: 'Mr. Winston', logo: '/images/integrations/pos/mr-winston.webp', slug: 'mr-winston' },
  { name: 'Hypersoft', logo: '/images/integrations/pos/hypersoft.webp', slug: 'hypersoft' },
];

export default function IntegrationsSection({ 
  translations, 
  altTexts,
  background = 'primary',
  locale,
  ctaId = 'cta_integrations',
  ctaPosition = 'after_integrations',
  linkCards = true,
}: IntegrationsSectionProps) {
  // Derive locale from URL when not explicitly provided. This guarantees that
  // integration links always point to the current-locale slug
  // (/sv/integrationer/..., /de/integrationen/..., /nl/integrations/..., /integrations/...)
  // instead of leaking to the EN root because of a missing prop on the caller.
  const params = useParams();
  const paramLocale = params?.locale as string | undefined;
  const effectiveLocale: Locale =
    locale ?? (paramLocale && (supportedLocales as readonly string[]).includes(paramLocale)
      ? (paramLocale as Locale)
      : 'en');

  return (
    <Section background={background} className={styles.integrationsSection}>
      <Container>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>{translations.label}</span>
          <Heading as="h2" className={styles.sectionTitle}>
            {translations.title}
          </Heading>
          <Text size="lg" color="secondary" className={styles.integrationsDescription}>
            {translations.description}
          </Text>
        </div>

        <div className={styles.integrationsGrid}>
          {integrations.map((partner, index) => {
            const cardContent = (
              <>
                <div className={styles.integrationLogoWrapper}>
                  <Image
                    src={partner.logo}
                    alt={altTexts?.[normalizePartnerName(partner.name)] || `${partner.name} integration`}
                    width={200}
                    height={100}
                    className={styles.integrationLogo}
                    style={{ objectFit: 'contain' }}
                    loading={index < 8 ? 'eager' : 'lazy'}
                    sizes="(max-width: 768px) 115px, (max-width: 1200px) 115px, 140px"
                  />
                </div>
                <span className={styles.integrationName}>{partner.name}</span>
              </>
            );

            if (!linkCards) {
              return (
                <div
                  key={index}
                  className={styles.integrationCard}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {cardContent}
                </div>
              );
            }

            return (
              <Link
                href={getIntegrationPath(partner.slug, effectiveLocale)}
                key={index}
                className={styles.integrationCard}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {cardContent}
              </Link>
            );
          })}
        </div>

        {translations.cta && (
          <div className={styles.ctaWrapper}>
            <Link 
              href={getLocalePath(effectiveLocale, getRoute('integrations', effectiveLocale))}
              onClick={() => trackCTAClick(ctaId, ctaPosition, 'primary')}
            >
              <Button
                variant="primary"
                size="lg"
                data-cta-id={ctaId}
                data-cta-position={ctaPosition}
                data-cta-variant="primary"
              >
                {translations.cta}
              </Button>
            </Link>
          </div>
        )}
      </Container>
    </Section>
  );
}

